import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const selectedCat = categories.find((c) => c.key === selected);

  function handleGo() {
    if (selectedCat) {
      window.open(selectedCat.link, "_blank", "noopener,nofollow");
    }
  }

  return (
    <section id="categories" className="scroll-mt-24">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        {/* Dropdown */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">
            {t("home.categories.title")}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            {t("home.categories.subtitle")}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1 text-left" ref={ref}>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between rounded-xl border border-background-300 bg-white px-4 py-3.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 cursor-pointer"
                aria-haspopup="listbox"
                aria-expanded={open}
              >
                <span className={selectedCat ? "font-medium text-foreground-900" : "text-foreground-400"}>
                  {selectedCat ? t(selectedCat.key) : t("home.categories.dropdownPlaceholder")}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-base text-foreground-500 transition-transform ${open ? "rotate-180" : ""}`}
                  aria-hidden="true"
                ></i>
              </button>

              {open && (
                <div className="absolute z-30 mt-2 max-h-72 w-full overflow-y-auto rounded-xl border border-background-200 bg-white py-1">
                  {categories.map((cat) => (
                    <button
                      key={cat.key}
                      type="button"
                      onClick={() => {
                        setSelected(cat.key);
                        setOpen(false);
                      }}
                      className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors cursor-pointer hover:bg-background-100 ${
                        selected === cat.key
                          ? "font-semibold text-primary-700"
                          : "text-foreground-700"
                      }`}
                    >
                      <span>{t(cat.key)}</span>
                      {selected === cat.key && (
                        <i className="ri-check-line text-primary-600" aria-hidden="true"></i>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleGo}
              disabled={!selectedCat}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-500 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
            >
              {t("home.categories.dropdownCta")}
              <i className="ri-arrow-right-up-line" aria-hidden="true"></i>
            </button>
          </div>
          <p className="mt-3 text-xs text-foreground-400">{t("home.categories.allExternal")}</p>
        </div>

        {/* Category grid */}
        <div className="mt-14">
          <h3 className="text-center text-lg font-bold text-foreground-900 md:text-xl">
            {t("home.categories.gridTitle")}
          </h3>
          <p className="mt-2 text-center text-sm text-foreground-500">
            {t("home.categories.gridSubtitle")}
          </p>

          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
            {categories.map((cat) => (
              <a
                key={cat.key}
                href={cat.link}
                target="_blank"
                rel="noopener nofollow"
                className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-white transition-transform hover:-translate-y-1 cursor-pointer"
              >
                <div className="relative h-28 w-full overflow-hidden sm:h-32">
                  <img
                    src={cat.image}
                    alt={t(cat.key)}
                    title={`${t(cat.key)} — ${t("common.siteName")}`}
                    className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                  <span className="truncate text-sm font-semibold text-foreground-800">
                    {t(cat.key)}
                  </span>
                  <i
                    className="ri-arrow-right-up-line shrink-0 text-sm text-foreground-400 transition-colors group-hover:text-primary-600"
                    aria-hidden="true"
                  ></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}